// Configuration spécifique pour l'authentification patient
import { PATIENT_AUTH_CONFIG, PATIENT_ENDPOINTS, SECURITY_CONFIG } from './constants';
import DateUtils from '@/utils/DateUtils';

// Types pour l'authentification patient
export interface PatientTokenData {
  token: string;
  tokenId?: string; // UUID du token dans la base
  patientId: string;
  expiresAt: string;
  patientName: string;
  email?: string;
  telephone?: string;
  createdAt?: string;
  accessCount?: number;
}

export interface PatientSession {
  isActive: boolean;
  tokenData: PatientTokenData | null;
  lastAccess: string;
  formProgress: FormProgress;
}

export interface FormProgress {
  currentStep: FormStep;
  completedSteps: FormStep[];
  totalSteps: number;
  progressPercentage: number;
  lastSavedAt?: string;
}

export type FormStep = 'form' | 'confirmation';

export interface ActivitePreferentielle {
  nom?: string;
  distanceMin?: string;
  distanceMax?: string;
}

// Interface fusionnée pour toutes les données du formulaire
export interface FormData {
  // Données personnelles (biométrie)
  nom?: string;
  prenom?: string;
  sexe?: 'M' | 'F';
  birthday?: Date | string;
  taille?: number; // En cm

  // Champs obligatoires (biométrie)
  photicEffect?: boolean; // Accepte les effets photiques
  glasses?: boolean; // Souhaite être indépendant des lunettes

  // Temps d'éveil et vision (biométrie)
  tempsEveil?: number; // En heures
  visionPres?: number; // Score 1-10
  visionIntermediaire?: number; // Score 1-10
  visionLoin?: number; // Score 1-10

  // Champs optionnels (biométrie)
  etatControlateral?: 'phaque' | 'pseudophaque' | 'monofocal';
  monovision?: boolean;
  oeilDominant?: 'droite' | 'gauche';

  // Données numériques (biométrie)
  larmes?: string;
  aberrationSpherique?: number;
  aberrationOrdreEleve?: number;
  diametrePupillaire?: number;
  longueurAxiale?: number;
  endothelium?: number;
  retineNerfOptique?: string;
  bump?: string;

  // Antécédents (biométrie)
  antecedantChirurgieCristallin?: boolean;
  courbeDefocus?: any; // À typer plus précisément selon les spécifications

  // Horaires (habitudes)
  heureReveil?: Date;
  heureCoucher?: Date;

  // Temps d'activités en minutes (habitudes)
  tempsSieste?: Date;
  tempsLecture?: Date;
  tempsTablette?: Date;
  tempsSmartphone?: Date;
  tempsAutreVisionPres?: Date;
  tempsDiscussion?: Date;
  tempsJeuxSociete?: Date;
  tempsOrdinateur?: Date;
  tempsCuisine?: Date;
  tempsConduite?: Date;
  tempsTelevision?: Date;

  // Activités préférentielles avec distances précises (habitudes)
  activitesPreferentielles?: ActivitePreferentielle[];
}

// Interface complète des données du formulaire patient
export interface PatientFormData {
  form: FormData;
  metadata: {
    currentStep: FormStep;
    completedSteps: FormStep[];
    startedAt: string;
    lastModifiedAt: string;
    autoSaveEnabled: boolean;
    validationErrors: Record<string, string>;
  };
}

// Utilitaires de validation
export class PatientFormValidator {
  static validateFormStep(data: FormData): ValidationResult {
    const errors: Record<string, string> = {};

    // Validation des champs biométrie obligatoires
    const requiredBiometrieFields = PATIENT_AUTH_CONFIG.VALIDATION.REQUIRED_BIOMETRIE_FIELDS;
    for (const field of requiredBiometrieFields) {
      if (data[field] === undefined || data[field] === null) {
        errors[field] = `Le champ ${field} est obligatoire`;
      }
    }

    // Validation des champs habitudes obligatoires
    const requiredHabitudesFields = PATIENT_AUTH_CONFIG.VALIDATION.REQUIRED_HABITUDES_FIELDS;
    for (const field of requiredHabitudesFields) {
      if (!data[field]) {
        errors[field] = `Le champ ${field} est obligatoire`;
      }
    }

    // Validations spécifiques biométrie
    if (data.aberrationSpherique !== undefined && (data.aberrationSpherique < -10 || data.aberrationSpherique > 10)) {
      errors.aberrationSpherique = 'L\'aberration sphérique doit être entre -10 et 10 μm';
    }

    if (data.diametrePupillaire !== undefined && (data.diametrePupillaire < 1 || data.diametrePupillaire > 10)) {
      errors.diametrePupillaire = 'Le diamètre pupillaire doit être entre 1 et 10 mm';
    }

    if (data.longueurAxiale !== undefined && (data.longueurAxiale < 15 || data.longueurAxiale > 35)) {
      errors.longueurAxiale = 'La longueur axiale doit être entre 15 et 35 mm';
    }

    // Validation des horaires
    if (data.heureReveil && data.heureCoucher) {
      const reveil = new Date(`1970-01-01T${data.heureReveil}:00`);
      const coucher = new Date(`1970-01-01T${data.heureCoucher}:00`);

      if (reveil >= coucher) {
        errors.heureReveil = 'L\'heure de réveil doit être antérieure à l\'heure de coucher';
      }
    }

    // Validation des temps d'activités (max 24h = 1440 minutes)
    const timeFields = [
      'tempsLecture', 'tempsOrdinateur', 'tempsSmartphone', 'tempsTablette',
      'tempsTelevision', 'tempsConduite', 'tempsCuisine', 'tempsDiscussion',
      'tempsJeuxSociete', 'tempsAutreVisionPres', 'tempsSieste'
    ] as const;

    const totalTime = timeFields.reduce((sum, field) => sum + (data[field] ? DateUtils.getMinutes(data[field]) : 0), 0);
    if (totalTime > 1440) {
      errors.tempsTotal = 'Le temps total d\'activités ne peut pas dépasser 24h';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  }

  // Aliases pour compatibilité
  static validateBiometrieStep = PatientFormValidator.validateFormStep;
  static validateHabitudesStep = PatientFormValidator.validateFormStep;
  
  static validateToken(token: string): boolean {
    if (!token || token.length < SECURITY_CONFIG.TOKEN_VALIDATION.MIN_LENGTH) {
      return false;
    }
    
    if (token.length > SECURITY_CONFIG.TOKEN_VALIDATION.MAX_LENGTH) {
      return false;
    }
    
    return SECURITY_CONFIG.TOKEN_VALIDATION.ALLOWED_CHARS.test(token);
  }
}

interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

// Utilitaires de gestion de session
export class PatientSessionManager {
  static calculateProgress(data: PatientFormData): FormProgress {
    const steps: FormStep[] = ['form', 'confirmation'];
    const completedCount = data.metadata.completedSteps.length;

    return {
      currentStep: data.metadata.currentStep,
      completedSteps: data.metadata.completedSteps,
      totalSteps: steps.length,
      progressPercentage: Math.round((completedCount / steps.length) * 100),
      lastSavedAt: data.metadata.lastModifiedAt,
    };
  }

  static getNextStep(currentStep: FormStep): FormStep | null {
    const steps: FormStep[] = ['form', 'confirmation'];
    const currentIndex = steps.indexOf(currentStep);

    if (currentIndex === -1 || currentIndex >= steps.length - 1) {
      return null;
    }

    return steps[currentIndex + 1];
  }

  static getPreviousStep(currentStep: FormStep): FormStep | null {
    const steps: FormStep[] = ['form', 'confirmation'];
    const currentIndex = steps.indexOf(currentStep);

    if (currentIndex <= 0) {
      return null;
    }

    return steps[currentIndex - 1];
  }
}